/**
 * HeatingCurve Component
 * Weather compensation / heating curve visualization
 * Shows supply temperature vs outdoor temperature and resulting COP
 */

import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Drawer, Space, Button, Tooltip, Switch } from "antd";

import { ArrowLeftOutlined, LineChartOutlined, CompressOutlined, ExpandOutlined, CloseOutlined } from "@ant-design/icons";
import { 
  calculateCOP, 
  calculateRadiatorHeatTransfer, 
  calculateVolumetricFlow, 
  calculateEnvelopeLoss, 
  calculateVentilationLoss, 
  calculateRoomVolume 
} from "./utils";
import styles from "./HeatPumpSimulation.module.css";

// Chart dimensions
const CHART_WIDTH = 340;
const CHART_HEIGHT = 240;
const CHART_MARGIN = { top: 20, right: 25, bottom: 40, left: 50 };
const PLOT_WIDTH = CHART_WIDTH - CHART_MARGIN.left - CHART_MARGIN.right;
const PLOT_HEIGHT = CHART_HEIGHT - CHART_MARGIN.top - CHART_MARGIN.bottom;

// Temperature ranges
const OUTDOOR_TEMP_MIN = -15;
const OUTDOOR_TEMP_MAX = 35;
const SUPPLY_TEMP_MIN = 20;
const SUPPLY_TEMP_MAX = 75;

/**
 * Calculate optimal supply temperature for continuous weather compensation
 * Takes into account current room temperature vs target temperature
 * Adjusts supply temperature to provide enough heat to reach AND maintain target
 */
export function calculateOptimalSupplyTemperature({
  currentRoomTemperature,
  targetTemperature,
  outdoorTemperature,
  uValue,
  totalArea,
  roofArea,
  wallArea,
  airChangeRate,
  radiatorCount,
  radiatorType,
  valvePosition,
  systemPressure,
}) {
  const volumetricFlow = calculateVolumetricFlow(systemPressure, 5, 15);
  
  // Calculate heat losses at target temperature (steady state requirement)
  const envelopeLossAtTarget = calculateEnvelopeLoss(uValue, totalArea, targetTemperature, outdoorTemperature, roofArea);
  const roomVolume = calculateRoomVolume(roofArea, wallArea);
  const ventilationLossAtTarget = calculateVentilationLoss(targetTemperature, outdoorTemperature, roomVolume, airChangeRate);
  const totalLossAtTarget = envelopeLossAtTarget.total + ventilationLossAtTarget;

  // Temperature error: how far we are from target
  const tempError = targetTemperature - currentRoomTemperature;
  
  // Additional heat needed to raise temperature (proportional control)
  // More aggressive when far from target, gentler when close
  const heatingBoost = Math.max(0, tempError * 150); // W per degree of error
  
  // Total heat needed: steady-state losses + boost to raise temperature
  const totalHeatNeeded = Math.max(0, totalLossAtTarget) + heatingBoost;

  // If no heating needed (outdoor warmer, room at/above target)
  if (totalHeatNeeded <= 0) {
    return SUPPLY_TEMP_MIN;
  }

  // Binary search for the supply temperature that provides this heat output
  let low = SUPPLY_TEMP_MIN;
  let high = SUPPLY_TEMP_MAX;
  let optimal = high;

  for (let i = 0; i < 20; i++) {
    const mid = (low + high) / 2;
    const heatResult = calculateRadiatorHeatTransfer({
      supplyTemperature: mid,
      roomTemperature: currentRoomTemperature,
      volumetricFlow,
      valvePosition,
      thermostatOpening: 1,
      radiatorCount,
      radiatorType,
    });
    const heatOutput = heatResult?.heatOutput || 0;

    if (heatOutput >= totalHeatNeeded) {
      optimal = mid;
      high = mid;
    } else {
      low = mid;
    }
  }

  // Round to nearest integer and clamp
  return Math.min(SUPPLY_TEMP_MAX, Math.max(SUPPLY_TEMP_MIN, Math.round(optimal)));
}

/**
 * Generate heating curve data points BASED ON ACTUAL SYSTEM PARAMETERS
 * For each outdoor temperature, calculates the minimum supply temperature
 * needed to achieve Q-net >= 0 (enough heat to maintain target room temp)
 */
function generateSystemBasedHeatingCurve({
  targetTemperature,
  uValue,
  totalArea,
  roofArea,
  wallArea,
  airChangeRate,
  radiatorCount,
  radiatorType,
  valvePosition,
  systemPressure,
}) {
  const outdoorTemps = [];
  const optimalSupplyTemps = [];
  const copValues = [];
  const qNetValues = [];

  const volumetricFlow = calculateVolumetricFlow(systemPressure, 5, 15);

  for (let tOut = OUTDOOR_TEMP_MIN; tOut <= OUTDOOR_TEMP_MAX; tOut += 2) {
    // Calculate total heat loss at this outdoor temperature
    const envelopeLoss = calculateEnvelopeLoss(uValue, totalArea, targetTemperature, tOut, roofArea);
    const roomVolume = calculateRoomVolume(roofArea, wallArea);
    const ventilationLoss = calculateVentilationLoss(targetTemperature, tOut, roomVolume, airChangeRate);
    const totalLoss = envelopeLoss.total + ventilationLoss;

    // Find minimum supply temperature that provides enough heat
    let optimalSupply = SUPPLY_TEMP_MIN;
    
    if (totalLoss > 0) {
      // Binary search for optimal supply temperature
      let low = SUPPLY_TEMP_MIN;
      let high = SUPPLY_TEMP_MAX;

      for (let i = 0; i < 20; i++) {
        const mid = (low + high) / 2;
        const heatResult = calculateRadiatorHeatTransfer({
          supplyTemperature: mid,
          roomTemperature: targetTemperature,
          volumetricFlow,
          valvePosition,
          thermostatOpening: 1,
          radiatorCount,
          radiatorType,
        });
        const heatOutput = heatResult?.heatOutput || 0;

        if (heatOutput >= totalLoss) {
          optimalSupply = mid;
          high = mid;
        } else {
          low = mid;
        }
      }
    }

    // Clamp supply temperature
    optimalSupply = Math.max(SUPPLY_TEMP_MIN, Math.min(SUPPLY_TEMP_MAX, optimalSupply));

    // Calculate COP at this operating point (with safety clamping)
    let cop = calculateCOP(optimalSupply, tOut);
    // Clamp COP to realistic range (theoretical max ~8-10 for modern heat pumps)
    cop = Math.max(1, Math.min(8, cop));

    // Calculate actual Q-net for verification
    const heatResult = calculateRadiatorHeatTransfer({
      supplyTemperature: optimalSupply,
      roomTemperature: targetTemperature,
      volumetricFlow,
      valvePosition,
      thermostatOpening: 1,
      radiatorCount,
      radiatorType,
    });
    const qNet = (heatResult?.heatOutput || 0) - totalLoss;

    outdoorTemps.push(tOut);
    optimalSupplyTemps.push(optimalSupply);
    copValues.push(cop);
    qNetValues.push(qNet);
  }

  return { outdoorTemps, optimalSupplyTemps, copValues, qNetValues };
}

/**
 * SVG Line Chart Component
 */
const LineChart = ({ 
  data, 
  xLabel, 
  yLabel, 
  xRange, 
  yRange, 
  lineColor = "#1890ff",
  showArea = false,
  areaColor = "rgba(24, 144, 255, 0.1)",
  currentX = null,
  currentY = null,
  secondaryData = null,
  secondaryColor = "#52c41a",
  title,
  yFormatter = (v) => v.toFixed(0),
  xFormatter = (v) => v.toFixed(0),
  yTickStep = null, // Custom step for y-axis ticks
  // Current value labels to display
  currentValueLabels = null, // Array of { label, value, color }
}) => {
  // Scale functions
  const scaleX = useCallback((val) => {
    return CHART_MARGIN.left + ((val - xRange[0]) / (xRange[1] - xRange[0])) * PLOT_WIDTH;
  }, [xRange]);

  const scaleY = useCallback((val) => {
    return CHART_HEIGHT - CHART_MARGIN.bottom - ((val - yRange[0]) / (yRange[1] - yRange[0])) * PLOT_HEIGHT;
  }, [yRange]);

  // Generate path
  const pathD = useMemo(() => {
    if (!data.x || data.x.length === 0) return "";
    let d = `M ${scaleX(data.x[0])} ${scaleY(data.y[0])}`;
    for (let i = 1; i < data.x.length; i++) {
      d += ` L ${scaleX(data.x[i])} ${scaleY(data.y[i])}`;
    }
    return d;
  }, [data, scaleX, scaleY]);

  const areaPathD = useMemo(() => {
    if (!showArea || !data.x || data.x.length === 0) return "";
    let d = `M ${scaleX(data.x[0])} ${scaleY(yRange[0])}`;
    d += ` L ${scaleX(data.x[0])} ${scaleY(data.y[0])}`;
    for (let i = 1; i < data.x.length; i++) {
      d += ` L ${scaleX(data.x[i])} ${scaleY(data.y[i])}`;
    }
    d += ` L ${scaleX(data.x[data.x.length - 1])} ${scaleY(yRange[0])} Z`;
    return d;
  }, [data, scaleX, scaleY, showArea, yRange]);

  const secondaryPathD = useMemo(() => {
    if (!secondaryData?.x || secondaryData.x.length === 0) return "";
    let d = `M ${scaleX(secondaryData.x[0])} ${scaleY(secondaryData.y[0])}`;
    for (let i = 1; i < secondaryData.x.length; i++) {
      d += ` L ${scaleX(secondaryData.x[i])} ${scaleY(secondaryData.y[i])}`;
    }
    return d;
  }, [secondaryData, scaleX, scaleY]);

  // X-axis ticks
  const xTicks = useMemo(() => {
    const ticks = [];
    const step = (xRange[1] - xRange[0]) / 5;
    for (let v = xRange[0]; v <= xRange[1]; v += step) {
      ticks.push(v);
    }
    return ticks;
  }, [xRange]);

  // Y-axis ticks
  const yTicks = useMemo(() => {
    const ticks = [];
    const step = yTickStep || (yRange[1] - yRange[0]) / 4;
    for (let v = yRange[0]; v <= yRange[1]; v += step) {
      ticks.push(v);
    }
    return ticks;
  }, [yRange, yTickStep]);

  return (
    <svg 
      width={CHART_WIDTH} 
      height={CHART_HEIGHT} 
      style={{ overflow: "visible" }}
    >
      {/* Title */}
      <text
        x={CHART_WIDTH / 2}
        y={12}
        textAnchor="middle"
        fontSize="11"
        fontWeight="600"
        fill="#333"
      >
        {title}
      </text>

      {/* Grid lines */}
      {yTicks.map((tick, i) => (
        <line
          key={`grid-y-${i}`}
          x1={CHART_MARGIN.left}
          y1={scaleY(tick)}
          x2={CHART_WIDTH - CHART_MARGIN.right}
          y2={scaleY(tick)}
          stroke="#e8e8e8"
          strokeDasharray="3,3"
        />
      ))}

      {/* Area fill */}
      {showArea && (
        <path d={areaPathD} fill={areaColor} />
      )}

      {/* Main data line */}
      <path
        d={pathD}
        fill="none"
        stroke={lineColor}
        strokeWidth={2.5}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Secondary data line (e.g., Q-net) */}
      {secondaryPathD && (
        <path
          d={secondaryPathD}
          fill="none"
          stroke={secondaryColor}
          strokeWidth={2}
          strokeDasharray="5,3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}

      {/* Current operating point */}
      {currentX !== null && currentY !== null && (
        <>
          {/* Vertical line */}
          <line
            x1={scaleX(currentX)}
            y1={CHART_MARGIN.top}
            x2={scaleX(currentX)}
            y2={CHART_HEIGHT - CHART_MARGIN.bottom}
            stroke="#ff4d4f"
            strokeWidth={1}
            strokeDasharray="4,2"
          />
          {/* Horizontal line */}
          <line
            x1={CHART_MARGIN.left}
            y1={scaleY(currentY)}
            x2={CHART_WIDTH - CHART_MARGIN.right}
            y2={scaleY(currentY)}
            stroke="#ff4d4f"
            strokeWidth={1}
            strokeDasharray="4,2"
          />
          {/* Point marker */}
          <circle
            cx={scaleX(currentX)}
            cy={scaleY(currentY)}
            r={6}
            fill="#ff4d4f"
            stroke="#fff"
            strokeWidth={2}
          />
          {/* Glow effect */}
          <circle
            cx={scaleX(currentX)}
            cy={scaleY(currentY)}
            r={10}
            fill="none"
            stroke="#ff4d4f"
            strokeWidth={1}
            opacity={0.4}
          />
        </>
      )}

      {/* X-axis */}
      <line
        x1={CHART_MARGIN.left}
        y1={CHART_HEIGHT - CHART_MARGIN.bottom}
        x2={CHART_WIDTH - CHART_MARGIN.right}
        y2={CHART_HEIGHT - CHART_MARGIN.bottom}
        stroke="#999"
        strokeWidth={1}
      />
      
      {/* X-axis ticks and labels */}
      {xTicks.map((tick, i) => (
        <g key={`x-${i}`}>
          <line
            x1={scaleX(tick)}
            y1={CHART_HEIGHT - CHART_MARGIN.bottom}
            x2={scaleX(tick)}
            y2={CHART_HEIGHT - CHART_MARGIN.bottom + 4}
            stroke="#999"
          />
          <text
            x={scaleX(tick)}
            y={CHART_HEIGHT - CHART_MARGIN.bottom + 16}
            textAnchor="middle"
            fontSize="10"
            fill="#666"
          >
            {xFormatter(tick)}
          </text>
        </g>
      ))}

      {/* X-axis label */}
      <text
        x={CHART_WIDTH / 2}
        y={CHART_HEIGHT - 5}
        textAnchor="middle"
        fontSize="11"
        fill="#666"
      >
        {xLabel}
      </text>

      {/* Y-axis */}
      <line
        x1={CHART_MARGIN.left}
        y1={CHART_MARGIN.top}
        x2={CHART_MARGIN.left}
        y2={CHART_HEIGHT - CHART_MARGIN.bottom}
        stroke="#999"
        strokeWidth={1}
      />

      {/* Y-axis ticks and labels */}
      {yTicks.map((tick, i) => (
        <g key={`y-${i}`}>
          <line
            x1={CHART_MARGIN.left - 4}
            y1={scaleY(tick)}
            x2={CHART_MARGIN.left}
            y2={scaleY(tick)}
            stroke="#999"
          />
          <text
            x={CHART_MARGIN.left - 8}
            y={scaleY(tick) + 3}
            textAnchor="end"
            fontSize="10"
            fill="#666"
          >
            {yFormatter(tick)}
          </text>
        </g>
      ))}

      {/* Y-axis label */}
      <text
        x={-CHART_HEIGHT / 2}
        y={12}
        textAnchor="middle"
        fontSize="11"
        fill="#666"
        transform="rotate(-90)"
      >
        {yLabel}
      </text>

      {/* Current value display box */}
      {currentValueLabels && currentValueLabels.length > 0 && (
        <g>
          {/* Background */}
          <rect
            x={CHART_WIDTH - CHART_MARGIN.right - 95}
            y={CHART_MARGIN.top + 5}
            width={90}
            height={14 * currentValueLabels.length + 8}
            rx={4}
            fill="rgba(255,255,255,0.95)"
            stroke="#d9d9d9"
            strokeWidth={1}
          />
          {/* Values */}
          {currentValueLabels.map((item, i) => (
            <text
              key={i}
              x={CHART_WIDTH - CHART_MARGIN.right - 88}
              y={CHART_MARGIN.top + 18 + i * 14}
              fontSize="10"
              fill="#333"
            >
              {item.label}: <tspan fill={item.color} fontWeight="600">{item.value}</tspan>
            </text>
          ))}
        </g>
      )}
    </svg>
  );
};

/**
 * Heating Curve Visualization Modal
 */
export const HeatingCurveModal = ({
  visible,
  onClose,
  // Current parameters
  outdoorTemperature,
  targetTemperature,
  currentRoomTemperature,
  supplyTemperature,
  onSupplyTemperatureChange,
  // Building parameters
  uValue,
  totalArea,
  roofArea,
  wallArea,
  airChangeRate,
  // Radiator parameters
  radiatorCount,
  radiatorType,
  valvePosition,
  systemPressure,
  // Continuous weather compensation
  autoWeatherCompensation,
  onAutoWeatherCompensationChange,
}) => {
  const { t } = useTranslation();

  // Generate system-based curve data (calculated from actual parameters!)
  const curveData = useMemo(() => {
    return generateSystemBasedHeatingCurve({
      targetTemperature,
      uValue,
      totalArea,
      roofArea,
      wallArea,
      airChangeRate,
      radiatorCount,
      radiatorType,
      valvePosition,
      systemPressure,
    });
  }, [targetTemperature, uValue, totalArea, roofArea, wallArea, airChangeRate, radiatorCount, radiatorType, valvePosition, systemPressure]);

  // Find optimal supply temperature for current outdoor temperature from the curve
  const optimalSupplyTemp = useMemo(() => {
    // Interpolate from curve data
    const idx = curveData.outdoorTemps.findIndex(t => t >= outdoorTemperature);
    if (idx === 0) return curveData.optimalSupplyTemps[0];
    if (idx === -1) return curveData.optimalSupplyTemps[curveData.optimalSupplyTemps.length - 1];
    
    // Linear interpolation
    const t0 = curveData.outdoorTemps[idx - 1];
    const t1 = curveData.outdoorTemps[idx];
    const s0 = curveData.optimalSupplyTemps[idx - 1];
    const s1 = curveData.optimalSupplyTemps[idx];
    const ratio = (outdoorTemperature - t0) / (t1 - t0);
    return s0 + ratio * (s1 - s0);
  }, [curveData, outdoorTemperature]);

  // Calculate current COP (with clamping for display)
  const currentCOP = useMemo(() => {
    const cop = calculateCOP(supplyTemperature, outdoorTemperature);
    return Math.max(1, Math.min(8, cop));
  }, [supplyTemperature, outdoorTemperature]);

  // Calculate optimal COP (if using the curve's suggested supply temp)
  const optimalCOP = useMemo(() => {
    const cop = calculateCOP(optimalSupplyTemp, outdoorTemperature);
    return Math.max(1, Math.min(8, cop));
  }, [optimalSupplyTemp, outdoorTemperature]);

  // State for minimized mode (floating charts panel)
  const [isMinimized, setIsMinimized] = useState(false);

  // Charts component (reusable for both modes)
  const ChartsPanel = ({ compact = false }) => (
    <div style={{ display: 'flex', gap: compact ? 12 : 24, justifyContent: 'center', flexDirection: compact ? 'column' : 'row' }}>
      {/* Heating Curve Chart: Supply Temp vs Outdoor Temp */}
      <div>
        <LineChart
          title={t('heatPumpSimulation.modals.heatingCurve.chartTitle.supplyVsOutdoor')}
          data={{ x: curveData.outdoorTemps, y: curveData.optimalSupplyTemps }}
          xLabel={t('heatPumpSimulation.modals.heatingCurve.outdoorTemp')}
          yLabel={t('heatPumpSimulation.modals.heatingCurve.supplyTemp')}
          xRange={[OUTDOOR_TEMP_MIN, OUTDOOR_TEMP_MAX]}
          yRange={[SUPPLY_TEMP_MIN, SUPPLY_TEMP_MAX]}
          yTickStep={10}
          lineColor="#ff7a45"
          showArea={true}
          areaColor="rgba(255, 122, 69, 0.15)"
          currentX={outdoorTemperature}
          currentY={supplyTemperature}
          xFormatter={(v) => `${v}`}
          yFormatter={(v) => `${v}`}
          currentValueLabels={[
            { label: t('heatPumpSimulation.modals.heatingCurve.tOut'), value: `${outdoorTemperature.toFixed(0)}°C`, color: '#1890ff' },
            { label: t('heatPumpSimulation.modals.heatingCurve.tSupply'), value: `${supplyTemperature.toFixed(0)}°C`, color: '#ff7a45' },
          ]}
        />
      </div>

      {/* COP vs Outdoor Temperature Chart */}
      <div>
        <LineChart
          title={t('heatPumpSimulation.modals.heatingCurve.chartTitle.copVsOutdoor')}
          data={{
            x: curveData.outdoorTemps,
            y: curveData.copValues,
          }}
          xLabel={t('heatPumpSimulation.modals.heatingCurve.outdoorTemp')}
          yLabel={t('heatPumpSimulation.modals.heatingCurve.cop')}
          xRange={[OUTDOOR_TEMP_MIN, OUTDOOR_TEMP_MAX]}
          yRange={[1, 8]}
          lineColor="#52c41a"
          showArea={true}
          areaColor="rgba(82, 196, 26, 0.15)"
          currentX={outdoorTemperature}
          currentY={currentCOP}
          xFormatter={(v) => `${v}`}
          yFormatter={(v) => v.toFixed(1)}
          currentValueLabels={[
            { label: 'T out', value: `${outdoorTemperature.toFixed(0)}°C`, color: '#1890ff' },
            { label: 'COP', value: currentCOP.toFixed(2), color: '#52c41a' },
          ]}
        />
      </div>
    </div>
  );

  // Render minimized floating panel
  if (isMinimized && visible) {
    return (
      <div style={{
        position: 'fixed',
        top: 80,
        right: 20,
        zIndex: 1000,
        background: 'rgba(255, 255, 255, 0.98)',
        borderRadius: 12,
        boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
        padding: '12px 16px',
        border: '1px solid #e8e8e8',
        maxWidth: 380,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <Space size="small">
            <LineChartOutlined style={{ color: '#1890ff', fontSize: 14 }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#0050b3' }}>{t('heatPumpSimulation.modals.heatingCurve.minimized.heatingCurves')}</span>
            {autoWeatherCompensation && (
              <span style={{ fontSize: 9, color: '#52c41a', background: '#f6ffed', padding: '1px 4px', borderRadius: 3 }}>● {t('heatPumpSimulation.modals.heatingCurve.minimized.auto')}</span>
            )}
          </Space>
          <Space size={4}>
            <Tooltip title={t('heatPumpSimulation.modals.heatingCurve.minimized.expandTooltip')} color="white">
              <Button 
                type="text" 
                size="small" 
                icon={<ExpandOutlined style={{ fontSize: 12 }} />} 
                onClick={() => setIsMinimized(false)}
                style={{ padding: '2px 6px', height: 'auto' }}
              />
            </Tooltip>
            <Tooltip title={t('heatPumpSimulation.modals.heatingCurve.minimized.closeTooltip')} color="white">
              <Button 
                type="text" 
                size="small" 
                icon={<CloseOutlined style={{ fontSize: 12 }} />} 
                onClick={onClose}
                style={{ padding: '2px 6px', height: 'auto' }}
              />
            </Tooltip>
          </Space>
        </div>
        
        {/* Charts */}
        <ChartsPanel compact={true} />
        
        {/* Quick stats */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-around', 
          marginTop: 8, 
          padding: '6px 8px', 
          background: '#fafafa', 
          borderRadius: 6,
          fontSize: 10
        }}>
          <span>{t('heatPumpSimulation.modals.heatingCurve.minimized.copLabel')} <strong style={{ color: currentCOP > 3 ? '#52c41a' : '#faad14' }}>{currentCOP.toFixed(2)}</strong></span>
          <span>{t('heatPumpSimulation.modals.heatingCurve.minimized.optimalLabel')} <strong style={{ color: '#ff7a45' }}>{optimalSupplyTemp.toFixed(0)}°C</strong></span>
          <span>{t('heatPumpSimulation.modals.heatingCurve.minimized.roomLabel')} <strong style={{ color: '#1890ff' }}>{currentRoomTemperature?.toFixed(1) || '--'}°C</strong></span>
        </div>
      </div>
    );
  }

  return (
    <Drawer
      title={
        <Space direction="vertical" size={0}>
          <Space size="small" align="center">
            <LineChartOutlined style={{ color: '#1890ff', fontSize: '18px' }} />
            <span style={{ fontSize: '18px', fontWeight: 600, color: '#0050b3' }}>{t('heatPumpSimulation.modals.heatingCurve.drawerTitle')}</span>
          </Space>
          <span style={{ fontSize: '12px', color: '#666', fontWeight: 400 }}>
            {t('heatPumpSimulation.modals.heatingCurve.drawerSubtitle')}
          </span>
        </Space>
      }
      placement="left"
      width={800}
      onClose={onClose}
      open={visible}
      closeIcon={<ArrowLeftOutlined />}
      closable={true}
      extra={
        <Tooltip title={t('heatPumpSimulation.modals.heatingCurve.minimizeTooltip')} color="white">
          <Button 
            type="text" 
            icon={<CompressOutlined />} 
            onClick={() => setIsMinimized(true)}
            style={{ color: '#1890ff' }}
          >
            {t('heatPumpSimulation.modals.heatingCurve.float')}
          </Button>
        </Tooltip>
      }
    >
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        {/* Charts Row */}
        <div style={{ display: 'flex', gap: 24, justifyContent: 'center' }}>
          {/* Heating Curve Chart: Supply Temp vs Outdoor Temp */}
          <div className={styles.heatingCurveChart}>
            <LineChart
              title={t('heatPumpSimulation.modals.heatingCurve.optimalSupplyTitle')}
              data={{ x: curveData.outdoorTemps, y: curveData.optimalSupplyTemps }}
              xLabel={t('heatPumpSimulation.modals.heatingCurve.outdoorTemp')}
              yLabel={t('heatPumpSimulation.modals.heatingCurve.supplyTemp')}
              xRange={[OUTDOOR_TEMP_MIN, OUTDOOR_TEMP_MAX]}
              yRange={[SUPPLY_TEMP_MIN, SUPPLY_TEMP_MAX]}
              yTickStep={10}
              lineColor="#ff7a45"
              showArea={true}
              areaColor="rgba(255, 122, 69, 0.15)"
              currentX={outdoorTemperature}
              currentY={supplyTemperature}
              xFormatter={(v) => `${v}`}
              yFormatter={(v) => `${v}`}
              currentValueLabels={[
                { label: t('heatPumpSimulation.modals.heatingCurve.tOut'), value: `${outdoorTemperature.toFixed(0)}°C`, color: '#1890ff' },
                { label: t('heatPumpSimulation.modals.heatingCurve.tSupply'), value: `${supplyTemperature.toFixed(0)}°C`, color: '#ff7a45' },
              ]}
            />
            {/* Legend */}
            <Space direction="vertical" size={2} style={{ marginTop: 8 }}>
                <span style={{ fontSize: 9, color: '#666', fontStyle: 'italic', marginTop: 4 }}>
                ℹ {t('heatPumpSimulation.modals.heatingCurve.noHeatingBelow')}
                </span>
                <Space size="small">
                    <span style={{ width: 12, height: 3, backgroundColor: '#ff7a45', display: 'inline-block', borderRadius: 2 }} />
                    <span style={{ fontSize: 10, color: '#666' }}>{t('heatPumpSimulation.modals.heatingCurve.systemCalculatedCurve')}</span>
                </Space>
                <Space size="small">
                    <span style={{ width: 8, height: 8, backgroundColor: '#ff4d4f', display: 'inline-block', borderRadius: '50%' }} />
                    <span style={{ fontSize: 10, color: '#666' }}>{t('heatPumpSimulation.modals.heatingCurve.currentOperatingPoint')}</span>
                </Space>
            </Space>
          </div>

          {/* COP vs Outdoor Temperature Chart (indexed by same axis as supply curve) */}
          <div className={styles.heatingCurveChart}>
            <LineChart
              title={t('heatPumpSimulation.modals.heatingCurve.copTitle')}
              data={{
                x: curveData.outdoorTemps,
                y: curveData.copValues,
              }}
              xLabel={t('heatPumpSimulation.modals.heatingCurve.outdoorTemp')}
              yLabel={t('heatPumpSimulation.modals.heatingCurve.cop')}
              xRange={[OUTDOOR_TEMP_MIN, OUTDOOR_TEMP_MAX]}
              yRange={[1, 8]}
              lineColor="#52c41a"
              showArea={true}
              areaColor="rgba(82, 196, 26, 0.15)"
              currentX={outdoorTemperature}
              currentY={currentCOP}
              xFormatter={(v) => `${v}`}
              yFormatter={(v) => v.toFixed(1)}
              currentValueLabels={[
                { label: t('heatPumpSimulation.modals.heatingCurve.tOut'), value: `${outdoorTemperature.toFixed(0)}°C`, color: '#1890ff' },
                { label: t('heatPumpSimulation.modals.heatingCurve.cop'), value: currentCOP.toFixed(2), color: '#52c41a' },
              ]}
            />
            {/* Legend */}
            <Space direction="vertical" size={2} style={{ marginTop: 8 }}>
              <Space size="small">
                <span style={{ width: 12, height: 3, backgroundColor: '#52c41a', display: 'inline-block', borderRadius: 2 }} />
                <span style={{ fontSize: 10, color: '#666' }}>{t('heatPumpSimulation.modals.heatingCurve.copOnOptimalCurve')}</span>
              </Space>
              <span style={{ fontSize: 10, color: '#666' }}>{t('heatPumpSimulation.modals.heatingCurve.higherCopBetter')}</span>
            </Space>
          </div>
        </div>

      

        {/* Automatic Supply Control Toggle */}
        <div style={{ 
          background: autoWeatherCompensation 
            ? 'linear-gradient(135deg, #f6ffed 0%, #e6fffb 100%)' 
            : '#fafafa', 
          padding: '16px 20px', 
          borderRadius: 8,
          border: autoWeatherCompensation ? '2px solid #52c41a' : '2px solid #d9d9d9',
          transition: 'all 0.3s ease'
        }}>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Space size="middle" align="center">
                <Switch 
                  checked={autoWeatherCompensation}
                  onChange={onAutoWeatherCompensationChange}
                  checkedChildren="ON"
                  unCheckedChildren="OFF"
                />
                <span style={{ fontWeight: 600, color: autoWeatherCompensation ? '#52c41a' : '#333' }}>
                  {t('heatPumpSimulation.modals.heatingCurve.autoControl.title')}
                </span>
                {autoWeatherCompensation && (
                  <span style={{ 
                    fontSize: 11, 
                    color: '#52c41a', 
                    background: '#f6ffed', 
                    padding: '2px 8px', 
                    borderRadius: 4,
                    border: '1px solid #b7eb8f'
                  }}>
                    ● {t('heatPumpSimulation.modals.heatingCurve.autoControl.active')}
                  </span>
                )}
              </Space>
            </div>
            <p style={{ margin: 0, fontSize: 12, color: '#666' }} dangerouslySetInnerHTML={{ 
              __html: t('heatPumpSimulation.modals.heatingCurve.autoControl.description', { targetTemp: targetTemperature })
            }} />
            <ul style={{ margin: '4px 0 0 0', paddingLeft: 20, fontSize: 12, color: '#666' }}>
              <li dangerouslySetInnerHTML={{ 
                __html: t('heatPumpSimulation.modals.heatingCurve.autoControl.currentRoomTemp', { temp: currentRoomTemperature?.toFixed(1) || '--' })
              }} />
              <li dangerouslySetInnerHTML={{ 
                __html: t('heatPumpSimulation.modals.heatingCurve.autoControl.tempGapToTarget', { gap: ((targetTemperature - (currentRoomTemperature || 0))).toFixed(1) })
              }} />
              <li>{t('heatPumpSimulation.modals.heatingCurve.autoControl.boostBehavior')}</li>
            </ul>
          </Space>
        </div>

        {/* Heat Pump Efficiency Indicator - Compact Grid Layout */}
        <div style={{ 
          background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)', 
          padding: '14px 18px', 
          borderRadius: 8,
          border: '1px solid #91d5ff'
        }}>
          {/* Header Row */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: '#0050b3' }}>{t('heatPumpSimulation.modals.heatingCurve.efficiency.title')}</span>
            <Tooltip 
              color="white"
              overlayInnerStyle={{ width: 480, padding: 16 }}
              title={
                <div style={{ color: '#333' }}>
                  <p style={{ margin: '0 0 8px 0', fontWeight: 600, fontSize: 14, textAlign: 'center'}}>
                    {t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipTitle')}
                  </p>
                  <p style={{ margin: '0 0 12px 0', fontSize: 12, textAlign: 'center', color: '#666' }} dangerouslySetInnerHTML={{
                    __html: t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSubtitle')
                  }} />
                  
                  {/* Goal-oriented visual diagram */}
                  <svg width="448" height="180" style={{ display: 'block', margin: '0 auto 12px' }}>
                    {/* Step 1: The Problem */}
                    <text x="224" y="16" textAnchor="middle" fontSize="11" fontWeight="600" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipChallenge')}</text>
                    
                    {/* Cold outside */}
                    <rect x="15" y="30" width="70" height="55" fill="#e6f7ff" stroke="#1890ff" strokeWidth="2" rx="6" />
                    <text x="50" y="52" textAnchor="middle" fontSize="13" fontWeight="600" fill="#1890ff">{t('heatPumpSimulation.modals.heatingCurve.tOut')}</text>
                    <text x="50" y="68" textAnchor="middle" fontSize="11" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.tOutCold')}</text>
                    <text x="50" y="80" textAnchor="middle" fontSize="10" fill="#999">❄️</text>
                    
                    {/* Arrow: causes */}
                    <path d="M90 58 L115 58" stroke="#ff4d4f" strokeWidth="2" markerEnd="url(#arrowRed)" />
                    <text x="102" y="48" textAnchor="middle" fontSize="9" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.causesLabel')}</text>
                    
                    {/* Heat Loss */}
                    <rect x="120" y="30" width="85" height="55" fill="#fff1f0" stroke="#ff4d4f" strokeWidth="2" rx="6" />
                    <text x="162" y="52" textAnchor="middle" fontSize="13" fontWeight="600" fill="#ff4d4f">Q loss</text>
                    <text x="162" y="68" textAnchor="middle" fontSize="11" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.qLossLabel')}</text>
                    <text x="162" y="80" textAnchor="middle" fontSize="9" fill="#999">UA · ΔT</text>
                    
                    {/* Arrow: requires */}
                    <path d="M210 58 L235 58" stroke="#52c41a" strokeWidth="2" markerEnd="url(#arrowGreen)" />
                    <text x="222" y="48" textAnchor="middle" fontSize="9" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.needsLabel')}</text>
                    
                    {/* Heat Supply */}
                    <rect x="240" y="30" width="85" height="55" fill="#f6ffed" stroke="#52c41a" strokeWidth="2" rx="6" />
                    <text x="282" y="52" textAnchor="middle" fontSize="13" fontWeight="600" fill="#52c41a">Q heat</text>
                    <text x="282" y="68" textAnchor="middle" fontSize="11" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.qHeatLabel')}</text>
                    <text x="282" y="80" textAnchor="middle" fontSize="9" fill="#999">Q heat ≥ Q loss</text>
                    
                    {/* Arrow: from */}
                    <path d="M330 58 L355 58" stroke="#ff7a45" strokeWidth="2" markerEnd="url(#arrowOrange)" />
                    <text x="342" y="48" textAnchor="middle" fontSize="9" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.fromLabel')}</text>
                    
                    {/* Supply Temp */}
                    <rect x="360" y="30" width="75" height="55" fill="#fff7e6" stroke="#ff7a45" strokeWidth="2" rx="6" />
                    <text x="397" y="52" textAnchor="middle" fontSize="13" fontWeight="600" fill="#ff7a45">{t('heatPumpSimulation.modals.heatingCurve.tSupply')}</text>
                    <text x="397" y="68" textAnchor="middle" fontSize="11" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.tSupplyLabel')}</text>
                    
                    {/* Divider line */}
                    <line x1="30" y1="100" x2="420" y2="100" stroke="#e8e8e8" strokeWidth="1" strokeDasharray="4,4" />
                    
                    {/* Step 2: The Insight */}
                    <text x="224" y="118" textAnchor="middle" fontSize="11" fontWeight="600" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipInsight')}</text>
                    
                    {/* Lower T_supply box */}
                    <rect x="50" y="128" width="120" height="45" fill="#fff7e6" stroke="#ff7a45" strokeWidth="2" rx="6" />
                    <text x="110" y="148" textAnchor="middle" fontSize="12" fontWeight="600" fill="#ff7a45">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.lowerSupply')}</text>
                    <text x="110" y="164" textAnchor="middle" fontSize="11" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.minimize')}</text>
                    
                    {/* Arrow */}
                    <path d="M175 150 L210 150" stroke="#52c41a" strokeWidth="2" markerEnd="url(#arrowGreen)" />
                    
                    {/* Higher COP box */}
                    <rect x="215" y="128" width="120" height="45" fill="#f6ffed" stroke="#52c41a" strokeWidth="2" rx="6" />
                    <text x="275" y="148" textAnchor="middle" fontSize="12" fontWeight="600" fill="#52c41a">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.higherCop')}</text>
                    <text x="275" y="164" textAnchor="middle" fontSize="11" fill="#666">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.efficiency')}</text>
                    
                    {/* Arrow */}
                    <path d="M340 150 L375 150" stroke="#1890ff" strokeWidth="2" markerEnd="url(#arrowBlue)" />
                    
                    {/* Lower cost box */}
                    <rect x="380" y="128" width="60" height="45" fill="#e6f7ff" stroke="#1890ff" strokeWidth="2" rx="6" />
                    <text x="410" y="155" textAnchor="middle" fontSize="11" fontWeight="600" fill="#1890ff">{t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipSteps.lessCost')}</text>
                    
                    {/* Arrow markers */}
                    <defs>
                      <marker id="arrowRed" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                        <path d="M0,0 L8,4 L0,8 Z" fill="#ff4d4f" />
                      </marker>
                      <marker id="arrowGreen" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                        <path d="M0,0 L8,4 L0,8 Z" fill="#52c41a" />
                      </marker>
                      <marker id="arrowOrange" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                        <path d="M0,0 L8,4 L0,8 Z" fill="#ff7a45" />
                      </marker>
                      <marker id="arrowBlue" markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
                        <path d="M0,0 L8,4 L0,8 Z" fill="#1890ff" />
                      </marker>
                    </defs>
                  </svg>
                  
                  {/* Goal summary */}
                  <div style={{ background: '#fafafa', padding: '10px 12px', borderRadius: 6, fontSize: 11, border: '1px solid #f0f0f0' }} dangerouslySetInnerHTML={{
                    __html: t('heatPumpSimulation.modals.heatingCurve.efficiency.tooltipGoal')
                  }} />
                </div>
              }
            >
              <span style={{ fontSize: 12, color: '#1890ff', cursor: 'help' }}>ℹ {t('heatPumpSimulation.modals.heatingCurve.efficiency.title').split(' ').slice(-3).join(' ')}</span>
            </Tooltip>
          </div>
          
          {/* Metrics Grid - Full Width 3 Columns */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 1fr 1fr', 
            gap: 12,
            width: '100%'
          }}>
            {/* Current COP Card */}
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.9)', 
              borderRadius: 6, 
              padding: '12px 10px',
              textAlign: 'center',
              border: '1px solid #d9d9d9',
            }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }} dangerouslySetInnerHTML={{ 
                __html: t('heatPumpSimulation.modals.heatingCurve.efficiency.currentCop')
              }} />
              <div style={{ 
                fontSize: 26, 
                fontWeight: 700, 
                color: currentCOP > 3 ? '#52c41a' : currentCOP > 2 ? '#faad14' : '#ff4d4f',
                lineHeight: 1.2
              }}>
                {currentCOP.toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                {currentCOP > 3 ? t('heatPumpSimulation.modals.heatingCurve.efficiency.efficient') : currentCOP > 2 ? t('heatPumpSimulation.modals.heatingCurve.efficiency.moderate') : t('heatPumpSimulation.modals.heatingCurve.efficiency.low')}
              </div>
            </div>
            
            {/* Equilibrium T_supply Card */}
            <Tooltip title={t('heatPumpSimulation.modals.heatingCurve.efficiency.equilibriumTooltip')} color="white">
              <div style={{ 
                background: 'rgba(255, 255, 255, 0.9)', 
                borderRadius: 6, 
                padding: '12px 10px',
                textAlign: 'center',
                border: '1px solid #d9d9d9',
                cursor: 'help'
              }}>
                <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }} dangerouslySetInnerHTML={{ 
                  __html: t('heatPumpSimulation.modals.heatingCurve.efficiency.equilibriumSupply')
                }} />
                <div style={{ 
                  fontSize: 26, 
                  fontWeight: 700, 
                  color: '#ff7a45',
                  lineHeight: 1.2
                }}>
                  {optimalSupplyTemp.toFixed(0)}°C
                </div>
                <div style={{ fontSize: 11, color: '#888', marginTop: 2 }} dangerouslySetInnerHTML={{ 
                  __html: t('heatPumpSimulation.modals.heatingCurve.efficiency.toMaintain', { temp: targetTemperature })
                }} />
              </div>
            </Tooltip>
            
            {/* Potential COP Card */}
            <div style={{ 
              background: 'rgba(255, 255, 255, 0.9)', 
              borderRadius: 6, 
              padding: '12px 10px',
              textAlign: 'center',
              border: '1px solid #d9d9d9',
            }}>
              <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }} dangerouslySetInnerHTML={{ 
                __html: t('heatPumpSimulation.modals.heatingCurve.efficiency.potentialCop')
              }} />
              <div style={{ 
                fontSize: 26, 
                fontWeight: 700, 
                color: '#52c41a',
                lineHeight: 1.2
              }}>
                {optimalCOP.toFixed(2)}
              </div>
              <div style={{ fontSize: 11, color: '#888', marginTop: 2 }} dangerouslySetInnerHTML={{ 
                __html: t('heatPumpSimulation.modals.heatingCurve.efficiency.atOptimal')
              }} />
            </div>
          </div>
        </div>

        {/* Info box */}
        <div style={{ 
          background: '#fafafa', 
          padding: '9px 12px', 
          borderRadius: 8,
          border: '1px solid #f0f0f0',
          fontSize: 12,
          color: '#888'
        }}>
          <strong style={{ color: '#666' }}>{t('heatPumpSimulation.modals.heatingCurve.tip.title')}</strong>
          <span style={{ marginLeft: 6 }}>
            {t('heatPumpSimulation.modals.heatingCurve.tip.text')}
          </span>
        </div>
      </Space>
    </Drawer>
  );
};

HeatingCurveModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  outdoorTemperature: PropTypes.number.isRequired,
  targetTemperature: PropTypes.number.isRequired,
  currentRoomTemperature: PropTypes.number,
  supplyTemperature: PropTypes.number.isRequired,
  onSupplyTemperatureChange: PropTypes.func.isRequired,
  uValue: PropTypes.number.isRequired,
  totalArea: PropTypes.number.isRequired,
  roofArea: PropTypes.number.isRequired,
  wallArea: PropTypes.number.isRequired,
  airChangeRate: PropTypes.number.isRequired,
  radiatorCount: PropTypes.number.isRequired,
  radiatorType: PropTypes.string.isRequired,
  valvePosition: PropTypes.number.isRequired,
  systemPressure: PropTypes.number.isRequired,
  autoWeatherCompensation: PropTypes.bool.isRequired,
  onAutoWeatherCompensationChange: PropTypes.func.isRequired,
};

/**
 * Custom Automatic Supply Control Icon SVG
 */
const WeatherCompensationIcon = ({ active = false, size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
    {/* Thermometer left */}
    <rect x="3" y="4" width="3" height="12" rx="1.5" stroke={active ? "#52c41a" : "#8c8c8c"} strokeWidth="1.5" fill="none" />
    <rect x="4" y="10" width="1" height="5" rx="0.5" fill={active ? "#52c41a" : "#8c8c8c"} />
    
    {/* Arrow */}
    <path d="M8 10 L12 10" stroke={active ? "#52c41a" : "#8c8c8c"} strokeWidth="1.5" strokeLinecap="round" />
    <path d="M10 7.5 L12.5 10 L10 12.5" stroke={active ? "#52c41a" : "#8c8c8c"} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    
    {/* Thermometer right */}
    <rect x="14" y="4" width="3" height="12" rx="1.5" stroke={active ? "#52c41a" : "#8c8c8c"} strokeWidth="1.5" fill="none" />
    <rect x="15" y="7" width="1" height="8" rx="0.5" fill={active ? "#52c41a" : "#8c8c8c"} />
  </svg>
);

/**
 * Custom Heating Curve Icon SVG
 */
const HeatingCurveIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="none">
    {/* Axes */}
    <path d="M4 16 L4 5 M4 16 L15 16" stroke="#1890ff" strokeWidth="1.5" strokeLinecap="round" />
    
    {/* Curve */}
    <path d="M5 14 Q8 12 10 10 Q12 8 15 6" stroke="#1890ff" strokeWidth="2" strokeLinecap="round" fill="none" />
  </svg>
);

/**
 * Compact Auto-Adjust Button for ControlPanel
 */
export const AutoAdjustSupplyButton = ({
  onShowHeatingCurve,
  autoWeatherCompensation,
  onAutoWeatherCompensationChange,
  showAutoToggle = true,
  showHeatingCurveButton = true,
  autoToggleDisabled = false,
}) => {
  const { t } = useTranslation();

  if (!showAutoToggle && !showHeatingCurveButton) {
    return null;
  }
  
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      {/* Automatic Supply Control Toggle */}
      {showAutoToggle && (
        <Tooltip 
          title={autoWeatherCompensation ? t('heatPumpSimulation.modals.heatingCurve.autoControl.disableTooltip') : t('heatPumpSimulation.modals.heatingCurve.autoControl.enableTooltip')}
          color="white"
          overlayInnerStyle={{ color: '#333' }}
        >
          <div
            onClick={autoToggleDisabled ? undefined : () => onAutoWeatherCompensationChange(!autoWeatherCompensation)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              padding: '3px 8px',
              borderRadius: 4,
              cursor: autoToggleDisabled ? 'not-allowed' : 'pointer',
              opacity: autoToggleDisabled ? 0.5 : 1,
              transition: 'all 0.2s ease',
              background: autoWeatherCompensation ? '#f6ffed' : '#fafafa',
              border: autoWeatherCompensation ? '1px solid #52c41a' : '1px solid #d9d9d9',
            }}
          >
            <WeatherCompensationIcon active={autoWeatherCompensation} size={16} />
            <span style={{ 
              fontSize: 11, 
              fontWeight: 500, 
              color: autoWeatherCompensation ? '#52c41a' : '#8c8c8c',
            }}>
              {autoWeatherCompensation ? t('heatPumpSimulation.modals.heatingCurve.minimized.auto') : 'OFF'}
            </span>
          </div>
        </Tooltip>
      )}

      {/* Heating Curve Panel Button */}
      {showHeatingCurveButton && (
        <Tooltip 
          title={t('heatPumpSimulation.modals.heatingCurve.labels.openPanel')}
          color="white"
          overlayInnerStyle={{ color: '#333' }}
        >
          <div
            onClick={onShowHeatingCurve}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 26,
              height: 26,
              borderRadius: 4,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              background: '#f0f5ff',
              border: '1px solid #adc6ff',
            }}
          >
            <HeatingCurveIcon size={16} />
          </div>
        </Tooltip>
      )}
    </div>
  );
};

AutoAdjustSupplyButton.propTypes = {
  onShowHeatingCurve: PropTypes.func.isRequired,
  autoWeatherCompensation: PropTypes.bool.isRequired,
  onAutoWeatherCompensationChange: PropTypes.func.isRequired,
  showAutoToggle: PropTypes.bool,
  showHeatingCurveButton: PropTypes.bool,
  autoToggleDisabled: PropTypes.bool,
};

export default HeatingCurveModal;
